/**
 * DOM Guardrail for Module Federation remotes.
 *
 * Uses MutationObserver to detect, attribute, and clean up <style>, <script>,
 * and <link rel="stylesheet"> tags injected into document.head or document.body
 * by remote micro-frontend apps outside their designated mount container.
 *
 * Integrated into PageHost to run automatically for every remote — zero changes
 * required from remote app teams.
 */

/** Predicate that returns true if a node should be ignored by the guardrail. */
export type AllowlistPredicate = (node: Node) => boolean;

/** Callback invoked when a violation is detected. */
export type ViolationCallback = (event: DomViolation) => void;

export interface DomViolation {
    appName: string;
    tagName: string;
    parentName: string;
    node: Element;
    timestamp: number;
}

export interface DomGuardrailConfig {
    /** Extra predicates — if any returns true the node is ignored. */
    allowlist?: AllowlistPredicate[];
    /** Called for every violation (in addition to the console.warn). */
    onViolation?: ViolationCallback;
}

interface TrackedNode {
    type: 'style' | 'script' | 'link';
    timestamp: number;
}

/**
 * Tags we care about when injected into `<head>` or `<body>`.
 * Map from uppercase tag name to the tracked type.
 */
const TRACKED_TAGS: Record<string, TrackedNode['type']> = {
    STYLE: 'style',
    SCRIPT: 'script',
    LINK: 'link'
};

/**
 * `<link>` rel values that are harmless (resource hints / module preloads).
 * These are allowlisted by default regardless of custom predicates.
 */
const SAFE_LINK_RELS = new Set(['modulepreload', 'preload', 'prefetch', 'icon', 'manifest']);

function isElement(node: Node): node is Element {
    return node.nodeType === Node.ELEMENT_NODE;
}

/**
 * Returns true when a node should be **skipped** by the guardrail
 * according to the built-in allowlist rules.
 */
function isDefaultAllowlisted(node: Element): boolean {
    // Nodes explicitly owned by the portal shell (e.g. font styles from createShadowContainer).
    if (node.hasAttribute('data-portal-owned')) return true;

    // Font style tags injected by createShadowContainer with a known id pattern.
    if (node.id && /^aero-fonts-/.test(node.id)) return true;

    // Pendo guide overlays and companion nodes are injected outside remote containers.
    if (node.id === 'pendo-guide-container') return true;
    if (Array.from(node.classList).some(className => className.startsWith('_pendo-'))) return true;

    // Harmless <link> types (modulepreload, preload, prefetch, icon, manifest).
    if (node.tagName === 'LINK') {
        const rel = (node.getAttribute('rel') ?? '').toLowerCase();
        if (SAFE_LINK_RELS.has(rel)) return true;
    }

    return false;
}

function nodePreview(node: Element): string {
    const id = node.id ? ` id="${node.id}"` : '';
    const cls = node.className ? ` class="${node.className}"` : '';
    const rel = node.getAttribute('rel') ? ` rel="${node.getAttribute('rel')}"` : '';
    const href = node.getAttribute('href') ? ` href="${node.getAttribute('href')}"` : '';
    return `<${node.tagName.toLowerCase()}${id}${cls}${rel}${href}>`;
}

export class DomGuardrail {
    private readonly appName: string;
    private readonly containerEl: HTMLElement;
    private readonly config: DomGuardrailConfig;
    private readonly trackedNodes = new Map<Node, TrackedNode>();
    private headObserver: MutationObserver | null = null;
    private bodyObserver: MutationObserver | null = null;
    private active = false;

    constructor(appName: string, containerEl: HTMLElement, config: DomGuardrailConfig = {}) {
        this.appName = appName;
        this.containerEl = containerEl;
        this.config = config;
    }

    /** Begin observing `<head>` and `<body>` for rogue injections. */
    start(): void {
        if (this.active) return;
        this.active = true;

        const callback = (mutations: MutationRecord[]) => this.handleMutations(mutations);

        this.headObserver = new MutationObserver(callback);
        this.bodyObserver = new MutationObserver(callback);

        this.headObserver.observe(document.head, { childList: true });
        this.bodyObserver.observe(document.body, { childList: true });
    }

    /**
     * Stop observing and remove any tracked nodes still in the DOM.
     * Call this **after** the remote's own `unmount()` so it has a chance
     * to clean up first — the guardrail only sweeps leftovers.
     */
    stop(): { removed: number } {
        this.active = false;

        this.headObserver?.disconnect();
        this.bodyObserver?.disconnect();
        this.headObserver = null;
        this.bodyObserver = null;

        let removed = 0;
        for (const [node, tracked] of this.trackedNodes) {
            // Only remove scripts — style/link nodes are left in the DOM so remounted
            // apps that rely on Vite's one-time CSS injection retain their styles.
            if (tracked.type !== 'script') continue;
            if (node.parentNode) {
                node.parentNode.removeChild(node);
                removed++;
                if (isElement(node)) {
                    console.warn(`[DomGuardrail] <${this.appName}> orphan removed on unmount: ${nodePreview(node)}`);
                }
            }
        }
        this.trackedNodes.clear();

        return { removed };
    }

    // ---- internals ----

    private handleMutations(mutations: MutationRecord[]): void {
        if (!this.active) return;

        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                this.inspect(node);
            }
            // If a tracked node is removed by the remote itself, stop tracking it.
            for (const node of mutation.removedNodes) {
                this.trackedNodes.delete(node);
            }
        }
    }

    private inspect(node: Node): void {
        if (!isElement(node)) return;

        const trackedType = TRACKED_TAGS[node.tagName];
        if (!trackedType) return;

        // Only track <link> tags that are stylesheets (non-stylesheet links are safe).
        if (trackedType === 'link') {
            const rel = (node.getAttribute('rel') ?? '').toLowerCase();
            if (rel !== 'stylesheet') return;
        }

        // Skip nodes that live inside the remote's own mount container.
        if (this.containerEl.contains(node)) return;

        // Built-in allowlist.
        if (isDefaultAllowlisted(node)) return;

        // Custom allowlist predicates.
        if (this.config.allowlist?.some(fn => fn(node))) return;

        const parentName = node.parentElement?.tagName.toLowerCase() ?? 'unknown';
        const timestamp = Date.now();

        this.trackedNodes.set(node, { type: trackedType, timestamp });

        const violation: DomViolation = {
            appName: this.appName,
            tagName: node.tagName.toLowerCase(),
            parentName,
            node: node,
            timestamp
        };

        console.warn(`[DomGuardrail] <${this.appName}> injected a <${violation.tagName}> into <${parentName}>: ${nodePreview(node)}`);

        this.config.onViolation?.(violation);
    }
}
